/**
 * Open-Ended Activity Slide
 * Text response question
 */

import { MessageSquare, PenLine } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';

export default function OpenEndedSlide({
  selectedAnswer,
  onAnswerChange,
  slide,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  const { content } = slide;
  const text = selectedAnswer || '';
  const maxLength = content?.maxLength || 500;

  return (
    <div className={`max-w-2xl mx-auto mb-8 ${className}`}>
      {/* Activity type badge */}
      <div className="flex items-center justify-center mb-6">
        <div className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-600 px-4 py-1.5 rounded-full text-sm font-semibold">
          <PenLine className="w-4 h-4" />
          Write your response
        </div>
      </div>

      {/* Text area card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-card rounded-2xl border-2 border-border/60 p-5 shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-2 mb-3 text-muted-foreground">
          <MessageSquare className="w-4 h-4" />
          <span className="text-sm font-medium">Your Answer</span>
        </div>
        <textarea
          value={text}
          onChange={(e) => onAnswerChange(e.target.value)}
          disabled={hasSubmitted}
          placeholder={content?.placeholder || 'Type your answer here...'}
          className="w-full min-h-[160px] sm:min-h-[200px] p-4 rounded-xl border-2 border-border/40 bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none resize-none text-base transition-all placeholder:text-muted-foreground/60"
        />
        
        {/* Character count */}
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">
            {hasSubmitted ? '✅ Answer submitted' : 'Be detailed and thoughtful'}
          </p>
          <span className={`text-xs font-medium ${
            text.length > maxLength * 0.9 ? 'text-destructive' : 'text-muted-foreground'
          }`}>
            {text.length} / {maxLength}
          </span>
        </div>
      </motion.div>
    </div>
  );
}
